import { z } from "zod";
import { apiDelete, apiGet, apiPost, apiPut, apiPostFormData } from "../lib/api";

export const TestCallResponseSchema = z.object({
  call_id: z.string(),
  room_name: z.string(),
  livekit_url: z.string(),
  operator_access_token: z.string(),
  expires_at: z.string(),
  dispatched_at: z.string(),
});
export type TestCallResponse = z.infer<typeof TestCallResponseSchema>;

// ─── Core enums ────────────────────────────────────────────────────────────────

export const VoicePipelineModeSchema = z.enum(["tts_gated"]);
export type VoicePipelineMode = z.infer<typeof VoicePipelineModeSchema>;

export const SipTrunkChoiceSchema = z.enum(["telnyx", "signalwire"]);
export type SipTrunkChoice = z.infer<typeof SipTrunkChoiceSchema>;

export const EngagementStateSchema = z.enum([
  "draft",
  "pending_authorization",
  "active",
  "paused",
  "kill_switch_locked",
  "concluded",
  "revoked",
]);
export type EngagementState = z.infer<typeof EngagementStateSchema>;

export const AutonomyTierSchema = z.enum(["tier_1", "tier_2", "tier_3"]);
export type AutonomyTier = z.infer<typeof AutonomyTierSchema>;

// ─── Allowlist structures ───────────────────────────────────────────────────────

export const PhoneAllowlistSchema = z.object({
  allowed_phones_e164: z.array(z.string()),
  denied_phones_e164: z.array(z.string()),
});

export const EmailAllowlistSchema = z.object({
  allowed_emails: z.array(z.string()),
  allowed_domains: z.array(z.string()),
  denied_emails: z.array(z.string()),
  denied_domains: z.array(z.string()),
});

// ─── Channel + email config ─────────────────────────────────────────────────────

export const ChannelSchema = z.enum(["voice", "email", "video"]);
export type Channel = z.infer<typeof ChannelSchema>;

// Mirrors enforcer EmailConfig. webhook_bearer_secret is write-only: it is sent
// on create but is NEVER returned by a read (the detail record omits it).
export const EmailConfigSchema = z.object({
  sending_profile_id: z.string(),
  subject: z.string(),
  body_html: z.string(),
  body_text: z.string().nullable().default(null),
  relay_base_url: z.string(),
});
export type EmailConfig = z.infer<typeof EmailConfigSchema>;

// The create-form's email config also carries the write-only bearer secret.
export interface EmailConfigCreateBody {
  sending_profile_id: string;
  subject: string;
  body_html: string;
  body_text?: string | null;
  relay_base_url: string;
  webhook_bearer_secret: string;
}

// ─── Video config + consent + runtime (mirrors enforcer 3a/3b/3c) ───────────────

export const VideoConfigSchema = z.object({
  avatar_interaction_mode: z.enum(["pre_rendered", "realtime"]),
  avatar_provider: z.enum(["heygen"]).default("heygen"),
  // "talking_photo" = HeyGen Photo Avatar / Avatar IV (rendered from one still);
  // "avatar" = classic avatar from video footage. Defaults to "avatar" for
  // backward compatibility with configs saved before this field existed.
  avatar_type: z.enum(["avatar", "talking_photo"]).default("avatar"),
  avatar_id: z.string(),
  voice_id: z.string(),
  script: z.string().nullable().default(null),
  script_approved_at: z.string().nullable().default(null),
  script_approved_by: z.string().nullable().default(null),
  approved_script_sha256: z.string().nullable().default(null),
});
export type VideoConfig = z.infer<typeof VideoConfigSchema>;

export const ConsentAttestationSchema = z.object({
  sha256: z.string(),
  signed_by: z.string(),
  signed_at: z.string(),
});
export type ConsentAttestation = z.infer<typeof ConsentAttestationSchema>;

export const VideoConsentSchema = z.object({
  likeness_release: ConsentAttestationSchema.nullable().default(null),
  org_sponsor_signoff: ConsentAttestationSchema.nullable().default(null),
  vendor_consent_record: ConsentAttestationSchema.nullable().default(null),
  biometric_retention_days: z.number().int().nullable().default(null),
});
export type VideoConsent = z.infer<typeof VideoConsentSchema>;

export const VideoRenderSchema = z.object({
  heygen_video_id: z.string(),
  clip_s3_key: z.string().nullable().default(null),
  rendered_script_sha256: z.string().nullable().default(null),
  rendered_at: z.string().nullable().default(null),
  status: z.enum(["pending", "completed"]).default("pending"),
});
export type VideoRender = z.infer<typeof VideoRenderSchema>;

export const VideoDispatchSchema = z.object({
  bot_id: z.string(),
  meeting_url: z.string(),
  dispatched_at: z.string(),
  status: z.string().default("joining"),
  status_polled_at: z.string().nullable().default(null),
  left_at: z.string().nullable().default(null),
});
export type VideoDispatch = z.infer<typeof VideoDispatchSchema>;

// ─── Engagement record (full detail) ───────────────────────────────────────────

export const EngagementRecordSchema = z.object({
  engagement_id: z.string(),
  customer_id: z.string(),
  display_name: z.string(),
  state: EngagementStateSchema,
  autonomy_tier: AutonomyTierSchema,
  voice_pipeline_mode: VoicePipelineModeSchema.default("tts_gated"),
  sip_trunk_choice: SipTrunkChoiceSchema.nullable().default(null),
  created_at: z.string(),
  activated_at: z.string().nullable(),
  concluded_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
  persona_id: z.string().nullable(),
  phone_allowlist: PhoneAllowlistSchema.nullable(),
  email_allowlist: EmailAllowlistSchema.nullable(),
  agent_tools_enabled: z.array(z.string()).default([]),
  max_turns_per_call: z.number().int().positive().nullable().default(null),
  recording_enabled: z.boolean().default(false),
  recording_disclosure_required: z.boolean().default(true),
  streaming_gating_enabled: z.boolean().catch(false),
  is_template: z.boolean().default(false),
  channel: ChannelSchema.default("voice"),
  email_config: EmailConfigSchema.nullable().default(null),
  video_config: VideoConfigSchema.nullable().default(null),
  video_consent: VideoConsentSchema.nullable().default(null),
  video_render: VideoRenderSchema.nullable().default(null),
  video_dispatch: VideoDispatchSchema.nullable().default(null),
});
export type EngagementRecord = z.infer<typeof EngagementRecordSchema>;

export const AGENT_TOOL_NAMES = [
  "record_credential",
  "record_pii",
  "flag_corp_data",
  "end_call",
] as const;
export type AgentToolName = (typeof AGENT_TOOL_NAMES)[number];

// ─── List item (summary) ────────────────────────────────────────────────────────

export const EngagementSummarySchema = z.object({
  engagement_id: z.string(),
  customer_id: z.string(),
  display_name: z.string(),
  state: EngagementStateSchema,
  autonomy_tier: AutonomyTierSchema,
  channel: ChannelSchema.default("voice"),
  is_template: z.boolean(),
  created_at: z.string(),
});
export type EngagementSummary = z.infer<typeof EngagementSummarySchema>;

// ─── Response envelopes ─────────────────────────────────────────────────────────

export const EngagementListResponseSchema = z.object({
  items: z.array(EngagementSummarySchema),
  total: z.number(),
});

export const EngagementDetailResponseSchema = z.object({
  record: EngagementRecordSchema,
});

// ─── CSV preview response ───────────────────────────────────────────────────────

export const PhonePreviewRowSchema = z.object({
  phone: z.string(),
  label: z.string().optional(),
});

export const EmailPreviewRowSchema = z.object({
  email: z.string(),
  label: z.string().optional(),
});

export const InvalidRowSchema = z.object({
  row_number: z.number(),
  raw: z.string(),
  reason: z.string(),
});

export const PhonePreviewResponseSchema = z.object({
  preview_token: z.string(),
  valid: z.array(PhonePreviewRowSchema),
  duplicates: z.array(PhonePreviewRowSchema),
  invalid: z.array(InvalidRowSchema),
  total_rows: z.number(),
});
export type PhonePreviewResponse = z.infer<typeof PhonePreviewResponseSchema>;

export const EmailPreviewResponseSchema = z.object({
  preview_token: z.string(),
  valid: z.array(EmailPreviewRowSchema),
  duplicates: z.array(EmailPreviewRowSchema),
  invalid: z.array(InvalidRowSchema),
  total_rows: z.number(),
});
export type EmailPreviewResponse = z.infer<typeof EmailPreviewResponseSchema>;

// ─── State machine constants ────────────────────────────────────────────────────

/**
 * Valid v1 UI transitions. Mirrors enforcer.engagement.TRANSITIONS;
 * server sets the side-effect timestamps (concluded_at, revoked_at) and
 * builds the KillSwitchEvent. Kill-switch requires a reason — see the
 * confirm dialog in pages/Engagements/Detail.tsx.
 *
 * `draft` re-appears as a target on `pending_authorization` so an
 * operator can pull a draft back from review; the model treats that as a
 * regular state edit, not a typed transition.
 */
export const STATE_TRANSITIONS: Record<EngagementState, EngagementState[]> = {
  draft: ["pending_authorization", "revoked"],
  pending_authorization: ["active", "draft", "revoked"],
  active: ["paused", "concluded", "kill_switch_locked", "revoked"],
  paused: ["active", "concluded", "kill_switch_locked", "revoked"],
  kill_switch_locked: ["concluded", "revoked"],
  concluded: [],
  revoked: [],
};

/**
 * UI variant for a transition button. Drives color + ordering so the
 * destructive choices read as destructive.
 *   safe       — forward progress (Activate, Submit for Auth)
 *   neutral    — sideways (Pause, Resume, Back to Draft)
 *   warning    — irreversible close-out (Conclude)
 *   danger     — admin overrides (Revoke)
 *   critical   — emergency lock (Kill switch); requires reason
 */
export type TransitionVariant = "safe" | "neutral" | "warning" | "danger" | "critical";

export const TRANSITION_VARIANT: Record<EngagementState, TransitionVariant> = {
  draft: "neutral",
  pending_authorization: "safe",
  active: "safe",
  paused: "neutral",
  concluded: "warning",
  revoked: "danger",
  kill_switch_locked: "critical",
};

/**
 * Ordered left-to-right for the state machine visualization.
 * kill_switch_locked and revoked are terminal side-branches rendered separately.
 */
export const STATE_ORDER: EngagementState[] = [
  "draft",
  "pending_authorization",
  "active",
  "paused",
  "concluded",
];

// ─── API fetchers ───────────────────────────────────────────────────────────────

export function fetchEngagements() {
  return apiGet("/api/engagements", EngagementListResponseSchema);
}

export function fetchEngagement(id: string) {
  return apiGet(`/api/engagements/${id}`, EngagementDetailResponseSchema);
}

export function createEngagement(body: {
  customer_id: string;
  display_name: string;
  autonomy_tier: AutonomyTier;
  persona_id?: string | null;
  is_template?: boolean;
  channel?: Channel;
  email_config?: EmailConfigCreateBody;
}) {
  return apiPost("/api/engagements", EngagementDetailResponseSchema, body);
}

export function testCall(engagementId: string, targetId: string) {
  return apiPost(`/api/engagements/${engagementId}/test-call`, TestCallResponseSchema, {
    target_id: targetId,
  });
}

export function updateEngagement(
  id: string,
  body: {
    display_name?: string;
    persona_id?: string | null;
    voice_pipeline_mode?: VoicePipelineMode;
    sip_trunk_choice?: SipTrunkChoice | null;
    agent_tools_enabled?: AgentToolName[];
    max_turns_per_call?: number | null;
    recording_enabled?: boolean;
    recording_disclosure_required?: boolean;
    streaming_gating_enabled?: boolean;
    video_config?: VideoConfig;
    video_consent?: VideoConsent;
    channel?: Channel;
  },
) {
  return apiPut(`/api/engagements/${id}`, EngagementDetailResponseSchema, body);
}

export function transitionEngagement(
  id: string,
  body: { target_state: EngagementState; comment?: string; reason?: string },
) {
  return apiPost(
    `/api/engagements/${id}/transition`,
    EngagementDetailResponseSchema,
    body,
  );
}

export function previewPhoneAllowlist(id: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  return apiPostFormData(
    `/api/engagements/${id}/allowlist/phone/preview`,
    PhonePreviewResponseSchema,
    form,
  );
}

export function commitPhoneAllowlist(id: string, preview_token: string) {
  return apiPost(
    `/api/engagements/${id}/allowlist/phone/commit`,
    EngagementDetailResponseSchema,
    { preview_token },
  );
}

export function previewEmailAllowlist(id: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  return apiPostFormData(
    `/api/engagements/${id}/allowlist/email/preview`,
    EmailPreviewResponseSchema,
    form,
  );
}

export function commitEmailAllowlist(id: string, preview_token: string) {
  return apiPost(
    `/api/engagements/${id}/allowlist/email/commit`,
    EngagementDetailResponseSchema,
    { preview_token },
  );
}

/** Hard-delete a standalone engagement. 204, or 409 if not deletable in its state. */
export function deleteEngagement(id: string) {
  return apiDelete(`/api/engagements/${id}`);
}
